import {Card, Flag} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotEmpty} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {SourcingVehicleResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {PriceChart, PriceChartItem} from '@dms/shared';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {getFirstScrapedPrice, getPriceChanges} from './getFirstScrapedPrice';

interface PriceChangesDetailProps extends TestIdProps {
  vehicle: SourcingVehicleResponseBody;
  currentPrice: number;
  currency: string;
}

/** Price history card: days in the ad, the overall price change and the price chart. */
export function PriceChangesDetail(props: PriceChangesDetailProps) {
  const formatCurrency = useFormatCurrency();

  const vehiclePrice = props.currentPrice;
  const currency = props.currency;
  const priceChanges = getPriceChanges(props.vehicle);
  const firstScrapedPrice = getFirstScrapedPrice(props.vehicle, vehiclePrice);
  const chartData: PriceChartItem[] = isNotEmpty(priceChanges)
    ? [
        ...priceChanges,
        {
          date: new Date().getTime(),
          price: vehiclePrice,
        },
      ]
    : [];
  const discount = firstScrapedPrice - vehiclePrice;
  const daysInAdvertisement = props.vehicle.daysOnStock;
  const nDaysText = isNotNil(daysInAdvertisement)
    ? i18n.t('entity.vehicle.labels.nDaysInAdvertisement', {
        count: daysInAdvertisement,
      })
    : EMPTY_PLACEHOLDER;

  return (
    <Card>
      <VStack spacing={4} width="100%">
        <HStack spacing={1} align="flex-start" justify="space-between" width="100%">
          <VStack spacing={1}>
            <Heading size={4} color="primary">
              {i18n.t('entity.vehicle.labels.priceChanges')}
            </Heading>
            <Text size="xSmall" color="secondary">
              {nDaysText}
            </Text>
          </VStack>
          <Flag
            colorScheme={discount >= 0 ? 'orange' : 'red'}
            size="small"
            label={
              discount >= 0
                ? i18n.t('entity.vehicle.labels.discount', {
                    discount: formatCurrency(discount, currency),
                  })
                : i18n.t('entity.vehicle.labels.priceIncrease', {
                    increase: formatCurrency(-discount, currency),
                  })
            }
            data-testid={suffixTestId('discount', props)}
          />
        </HStack>
        <Box width="100%" height={30}>
          <PriceChart chartData={chartData} currency={currency} />
        </Box>
      </VStack>
    </Card>
  );
}
